<style scoped>
    .form span:after{
        content:"*";
        color:red;
    }
</style>

<template>
    <layout-internal-container>
        <div class="w-full flex flex-col gap-6">
            <div class="flex flex-col gap-4 md:flex-row justify-between items-start md:items-end">
                <div class="flex flex-col gap-4">
                    <span class="text-sm text-light font-medium">Here's your business performance analytics</span>
                </div>
                <div class="flex flex-row gap-1 items-center">
                    <IconCalendarEvent :stroke-width="1.8" :size="18" />
                    <span class="text-xs">Last updated 08/2024</span>
                </div>
            </div>

            <div>
                <div class="flex justify-between mb-4 items-center">
                    <div class="container-filter flex gap-4">
                        <div class="year-filter gap-2">
                            <span class="text-sm text-light required">
                                Type
                            </span>
    
                            <a-select size="medium" :name="'type'" :placeholder="'-- Choose Type --'" required v-model:value="typeFilter">
                                <a-select-option v-for="option in typeOptions" :key="option" :value="option">{{ option }}</a-select-option>
                            </a-select>
                        </div>
                    </div>

                    <a-button @click="modalCreate = true" type="primary" class="flex gap-1 items-center float-end">
                        <IconEdit :size="18"/>
                        Add or Edit Data
                    </a-button>
                </div>

                <a-table
                    class="ant-table-striped" 
                    :columns="displayedColumns" 
                    :data-source="performances"
                    :row-class-name="(_record, index) => (index % 2 === 1 ? 'table-striped' : null)"
                    bordered
                >
                    <template #bodyCell="{ column, record }">
                        <template v-if="column.dataIndex === 'detail'">
                            <div class="flex flex-row">
                                <a-button type="link" class="text-primary flex items-center" @click="fetchSummary(record.year, record?.quarter)">
                                    <IconFileTypePdf/>
            
                                    <span class="ms-1">
                                        Link to PDF File
                                    </span>
                                </a-button>
                            </div>
                        </template>
                    </template>
                </a-table>
            </div>
        </div>
    </layout-internal-container>
    <a-modal v-model:open="modalCreate" title="Update Business Performance">
        <div class="grid grid-cols-12 gap-4 my-8 form">
            <div class="col-span-12">
                <span class="text-sm text-light required">
                    Type
                </span>

                <a-select size="large" class="mt-2 w-full" :name="'type'" :placeholder="'-- Choose Type --'" required v-model:value="formField['type']">
                    <a-select-option v-for="(option, index) in typeOptions" :key="option" :value="index">{{ option }}</a-select-option>
                </a-select>
            </div>

            <div class="col-span-6">
                <span class="text-sm text-light required">
                    Year
                </span>

                <a-select size="large" class="mt-2 w-full" :name="'year'" :placeholder="'-- Choose Year --'" required v-model:value="formField['year']">
                    <a-select-option v-for="option in yearOptions" :key="option" :value="option">{{ option }}</a-select-option>
                </a-select>
            </div>
            
            <div class="col-span-6">
                <span class="text-sm text-light required">
                    Quarter
                </span>

                <a-select size="large" class="mt-2 w-full" :name="'quarter'" :placeholder="'-- Choose Quarter --'" required :disabled="formField['type'] == 0" v-model:value="formField['quarter']">
                    <a-select-option v-for="option in quarterOptions" :key="option" :value="option">{{ option }}</a-select-option>
                </a-select>
            </div>
            
            <div class="col-span-6">
                <span class="text-sm text-light required">  
                    Revenue
                </span>

                <a-input-number
                    size="large" bordered class="w-full mt-2"
                    v-model:value="formField['revenue']"
                    :formatter="value => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                    :parser="value => value.replace(/Rp\s?|(,*)/g, '')"
                />
            </div>
            
            <div class="col-span-6">
                <span class="text-sm text-light required">
                    Ebitda
                </span>

                <a-input-number
                    size="large" bordered class="w-full mt-2"
                    v-model:value="formField['ebitda']"
                    :formatter="value => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                    :parser="value => value.replace(/Rp\s?|(,*)/g, '')"
                />
            </div>

            <div class="col-span-6">
                <span class="text-sm text-light required">  
                    Net Profit
                </span>

                <a-input-number
                    size="large" bordered class="w-full mt-2"
                    v-model:value="formField['net_profit']"
                    :formatter="value => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                    :parser="value => value.replace(/Rp\s?|(,*)/g, '')"
                />
            </div>
            
            <div class="col-span-6">
                <span class="text-sm text-light required">
                    Gross Profit
                </span>

                <a-input-number
                    size="large" bordered class="w-full mt-2"
                    v-model:value="formField['gross_profit']"
                    :formatter="value => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                    :parser="value => value.replace(/Rp\s?|(,*)/g, '')"
                />
            </div>
            
            <div class="col-span-6">
                <span class="text-sm text-light required">
                    Net Profit Margin
                </span>

                <a-input-number
                    size="large" bordered class="w-full mt-2"
                    v-model:value="formField['net_profit_margin']"
                    :min="0"
                    :max="100"
                    :formatter="value => `${value}%`"
                    :parser="value => value.replace('%', '')"
                />
            </div>
            
            <div class="col-span-6">
                <span class="text-sm text-light required">
                    Gross Profit Margin
                </span>

                <a-input-number
                    size="large" bordered class="w-full mt-2"
                    v-model:value="formField['gross_profit_margin']"
                    :min="0"
                    :max="100"
                    :formatter="value => `${value}%`"
                    :parser="value => value.replace('%', '')"
                />
            </div>
            
            <div class="col-span-6">
                <span class="text-sm text-light required">
                    Number of full time employees
                </span>

                <a-input-number
                    size="large" bordered class="w-full mt-2"
                    v-model:value="formField['full_time_employees']"
                />
            </div>
            
            <div class="col-span-6">
                <span class="text-sm text-light required">
                    Number of part time employees
                </span>

                <a-input-number
                    size="large" bordered class="w-full mt-2"
                    v-model:value="formField['part_time_employees']"
                />
            </div>
            
            <div class="col-span-6">
                <span class="text-sm text-light required">
                    Number of beneficiaries
                </span>

                <a-input-number
                    size="large" bordered class="w-full mt-2"
                    v-model:value="formField['beneficiaries']"
                />
            </div>

            <div class="col-span-6">
                <span class="text-sm text-light required">
                    Active Partnership
                </span>

                <a-input-number
                    size="large" bordered class="w-full mt-2"
                    v-model:value="formField['active_partnerships']"
                />
            </div>
        </div>

        <template #footer>
            <a-button key="submit" type="primary" :loading="loading" @click="saveBusinessPerformance">Submit</a-button>
        </template>
    </a-modal>
</template>

<script setup>
    import axios from "@/plugins/axios";
    import { IconEdit, IconFileTypePdf, IconPlus } from '@tabler/icons-vue';
    import { message } from 'ant-design-vue';
    import { onMounted, ref, watch } from 'vue';
    import useAuth from "@/composables/useAuth"

    const loading = ref(false);
    const modalCreate = ref(false);
    const performances = ref([]);
    const displayedColumns = ref([]);

    const typeOptions = [
        'Annually',
        'Quarterly',
    ]

    const quarterOptions = [
        'Q1',
        'Q2',
        'Q3',
        'Q4',
    ]

    const yearOptions = ref(Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i));
    const resetFormField = { 
        revenue: 0, 
        ebitda: 0, 
        net_profit: 0, 
        gross_profit: 0, 
        net_profit_margin: 0, 
        gross_profit_margin: 0, 
        full_time_employees: 0, 
        part_time_employees: 0, 
        beneficiaries: 0,
        active_partnerships: 0,

    }
    
    const formField = ref({
        type: 0,
        year: yearOptions[0], 
        quarter: 'Q1', 
        ...resetFormField
    })

    const columns = [
        {
            title: 'Year',
            dataIndex: 'year',
            key: 'year',
        },
        {
            title: 'Quarter',
            dataIndex: 'quarter',
            key: 'quarter',
        },
        {
            title: 'Detail',
            dataIndex: 'detail',
            key: 'detail',
        },
    ];

    const typeFilter = ref(typeOptions[0])

    const saveBusinessPerformance = async () => {
        try {
            loading.value = true

            const formData = new FormData();

            Object.keys(formField.value).forEach((key) => {
                if (key == 'quarter' && formField.value.type == 0) {
                    return;
                }

                formData.append(key, formField.value[key]);
            });

            formData.append('currency', 'IDR');
            
            await axios.post('/business-performance/store', formData);

            message.success('Success update business performance!')
        } catch (error) {
            console.error(error)
            message.error(error?.response?.data?.message ?? 'An error occurred. Please try again later.')
        }
        
        setTimeout(() => {
            fetchBusinessPerformance()
        }, 500);
        
        loading.value = false
        modalCreate.value = false

        formField.value = {
            type: 0,
            year: yearOptions.value[0], 
            quarter: 'Q1', 
            ...resetFormField
        };
    }

    const fetchUserInfo = () => {
        try {
            const r = useAuth().get().user

            const yearNow = new Date().getFullYear()
            const yearEstbl = parseInt(JSON.parse(JSON.parse(r))?.year_estbl)
            yearOptions.value = Array.from({ length: yearNow - yearEstbl + 1 }, (_, i) => yearEstbl + i);

            formField.value.year = yearOptions.value[0]
        } catch (error) {
            console.error(error?.response?.data?.message)
        }
    }

    const fetchBusinessPerformance = async () => {
        try {
            const response = await axios.get('/business-performance/list', {
                params: {
                    type: typeFilter.value.toLowerCase(),
                }
            })

            performances.value = response.data.map((item, index) => ({
                key: (index + 1).toString(),
                year: item.year,
                quarter: item.quarter,
                detail: item.detail || ''
            }));

            // Display column
            displayedColumns.value = columns;

            if (typeFilter.value === 'Annually') {
                displayedColumns.value = columns.filter(column => column.dataIndex !== 'quarter');
            }
        } catch (error) {
            console.error('Error fetching Business Performance:', error)
        }
    }

    const fetchBusinessPerformanceDetail = async () => {
        try {
            if (!(modalCreate.value)) {
                return
            }

            const response = await axios.get('/business-performance/detail', {
                params: {
                    year: formField.value.year,
                    quarter: formField.value.type ? formField.value.quarter : '',
                }
            })

            formField.value = {
                ...formField.value,
                revenue: response.data.revenue || 0,
                ebitda: response.data.ebitda || 0,
                net_profit: response.data.net_profit || 0,
                net_profit_margin: response.data.net_profit_margin || 0,
                gross_profit: response.data.gross_profit || 0,
                gross_profit_margin: response.data.gross_profit_margin || 0,
                full_time_employees: response.data.full_time_employees || 0,
                part_time_employees: response.data.part_time_employees || 0,
                beneficiaries: response.data.beneficiaries || 0,
                active_partnerships: response.data.active_partnerships || 0,
            };
            
        } catch (error) {
            formField.value = {
                type: formField.value.type,
                year: formField.value.year, 
                quarter: formField.value.quarter, 
                ...resetFormField
            };

            console.error('Error fetching Business Performance Detail:', error)
        }
    }
    
    const fetchSummary = async (year, quarter = null) => {
        try {
            const response = await axios.get('/business-performance/summary', {
                params: {
                    year: year,
                    quarter: quarter,
                },
                responseType: 'blob',
            })

            const fileURL = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

            window.open(fileURL, '_blank');
        } catch (error) {
            console.error('Error fetching Business Performance:', error)
        }
    }

    watch(typeFilter, () => {
        fetchBusinessPerformance()
    }, { immediate: true })

    watch(
        [() => formField.value.quarter, () => formField.value.year, () => formField.value.type, () => modalCreate.value],
        () => {
            fetchBusinessPerformanceDetail();
        },
        { immediate: true }
    )

   onMounted(() => {
        fetchBusinessPerformance()
        fetchUserInfo()
   })
</script>

<style scoped>
/* You can adjust any additional styles here to further fine-tune responsiveness */
</style>
